import { posthog } from '@/clients/shared-components/Posthog';
import { fetchJson } from '@/clients/util/xhr';
import { Customer, customerFlags } from '@/ifaces/customers';
import { DEFAULT_ENTITLEMENTS, Entitlements } from '@/ifaces/entitlements';
import { LanguageCode, toLanguageCode } from '@/ifaces/language';
import { OldProfile } from '@/ifaces/profile';
import { UserType } from '@/ifaces/user';
import { useQuery } from '@tanstack/react-query';
import {
  PropsWithChildren,
  createContext,
  createElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useLocalizationContext } from './use-localization-context';

type AuthedUserContextType = { ready: boolean; error: boolean } & AuthedUser;

export type AuthedUser = {
  user_id: string;
  email: string;
  entitlements: Entitlements;
  profile: OldProfile;
  isOnboarded: boolean;
  aiPolicy: boolean;
  user_type: UserType;
  customer_id: string;
  customer_name: string;
  customer: Customer & { pdfDownloadUrl?: string };
};

const AuthedUserContext = createContext<AuthedUserContextType>({
  ready: false,
  error: false,
  user_id: '',
  email: '',
  profile: {
    full_name: '',
    company: '',
    role: '',
    language_code: LanguageCode.English,
  },
  isOnboarded: false,
  aiPolicy: false,
  entitlements: DEFAULT_ENTITLEMENTS,
  user_type: UserType.Anonymous,
  customer_id: '',
  customer_name: '',
  customer: { id: '', name: '', ...customerFlags() },
});

export function useAuthedUserContext() {
  return useContext(AuthedUserContext);
}

export const AuthedUserContextProvider = (props: PropsWithChildren<{ english?: boolean }>) => {
  const query = useQuery<AuthedUser>({
    queryKey: ['authed-user'],
    queryFn: () => fetchJson('api/auth'),
  });

  const ready = query.isFetched && !query.isError && !!query.data;

  useEffect(() => {
    if (query.isError) {
      console.error('Failed to fetch authed user', query.error);
    }
  }, [query.isError, query.error]);

  const language_code = toLanguageCode(query.data?.profile?.language_code);
  const setCodeRef = useRef({ fn: useLocalizationContext().setCode });
  useEffect(() => {
    setCodeRef.current.fn(language_code);
  }, [language_code]);

  const value: AuthedUserContextType = useMemo(
    () => ({
      ready,
      error: query.isError,
      user_id: query.data?.user_id || '',
      email: query.data?.email || '',
      profile: {
        full_name: query.data?.profile?.full_name || '',
        company: query.data?.profile?.company || '',
        role: query.data?.profile?.role || '',
        language_code,
      },
      isOnboarded: !!query.data?.isOnboarded,
      aiPolicy: !!query.data?.aiPolicy,
      entitlements: {
        ...DEFAULT_ENTITLEMENTS,
        ...(query.data?.entitlements || {}),
      },
      user_type: query.data?.user_type || UserType.Anonymous,
      customer_id: query.data?.customer_id || '',
      customer_name: query.data?.customer_name || '',
      customer: {
        id: query.data?.customer?.id || '',
        name: query.data?.customer?.name || '',
        pdfDownloadUrl: query.data?.customer?.pdfDownloadUrl || undefined,
        ...customerFlags(query.data?.customer),
      },
    }),
    [query.data, query.isError, ready, language_code],
  );

  useEffect(() => {
    if (value.email) {
      posthog.identify(value.email);
    }
  }, [value.email]);

  return createElement(AuthedUserContext.Provider, { value }, props.children);
};
